import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BarChart3, Download, ExternalLink, ChevronDown } from 'lucide-react';
import Sidebar from './Sidebar';
import { useAnalytics } from './Analyticscontext';

const Analytics = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  
  const [activeTab, setActiveTab] = useState(tab || 'content');
   // ✅ GET ACCOUNT INFO FROM LOCALSTORAGE
  const [selectedAccountId, setSelectedAccountId] = useState(
    localStorage.getItem('selectedLinkedInAccountId')
  );
  const [accountType, setAccountType] = useState(
    localStorage.getItem('selectedAccountType')
  );

  const getTwoDaysAgo = () => {
    const date = new Date();
    date.setDate(date.getDate() - 2);
    return date.toISOString().split('T')[0];
  };
  
  const [dateRange, setDateRange] = useState({ 
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: getTwoDaysAgo()
  });
  const [tempDateRange, setTempDateRange] = useState({ ...dateRange });
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [visitorMetric, setVisitorMetric] = useState('page views');
  const [visitorPage, setVisitorPage] = useState('all pages');
  const [visitorType, setVisitorType] = useState('all visitors');
  const [showMetricDropdown, setShowMetricDropdown] = useState(false);
  const [showPageDropdown, setShowPageDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const [followerDemographic, setFollowerDemographic] = useState('Location');

  // ✅ USE CONTEXT
  const {
    contentData,
    visitorsData,
    followersData,
    loadingContent,
    loadingVisitors,
    loadingFollowers,
    isLoading, // Combined loading state
    fetchAllAnalytics,
    refreshAllAnalytics
  } = useAnalytics();
// ✅ LISTEN FOR ACCOUNT CHANGES
  useEffect(() => {
    const handleAccountChange = () => {
      const newAccountId = localStorage.getItem('selectedLinkedInAccountId');
      const newAccountType = localStorage.getItem('selectedAccountType');
      
      console.log('📍 Analytics detected account change');
      setSelectedAccountId(newAccountId);
      setAccountType(newAccountType);
      
      // Refresh analytics with new account
      const visitorParams = {
        metric: visitorMetric,
        page: visitorPage,
        type: visitorType
      };
      refreshAllAnalytics(dateRange, visitorParams, followerDemographic);
    };

    window.addEventListener('accountChanged', handleAccountChange);
    return () => window.removeEventListener('accountChanged', handleAccountChange);
  }, [dateRange, visitorMetric, visitorPage, visitorType, followerDemographic]);
  const N8N_BASE_URL = 'https://n8n.acespireconsulting.com/webhook';
   // ✅ ADD THESE WRAPPER FUNCTIONS FOR DROPDOWN FILTERS
  const fetchVisitorAnalytics = () => {
    console.log('👀 Visitor filter changed - refreshing visitor analytics');
    const visitorParams = {
      metric: visitorMetric,
      page: visitorPage,
      type: visitorType
    };
    // Re-fetch only visitors data with new filters
    refreshAllAnalytics(dateRange, visitorParams, followerDemographic);
  };

  const fetchFollowerAnalytics = () => {
    console.log('👥 Follower demographic changed - refreshing follower analytics');
    const visitorParams = {
      metric: visitorMetric,
      page: visitorPage,
      type: visitorType
    };
    // Re-fetch only followers data with new demographic
    refreshAllAnalytics(dateRange, visitorParams, followerDemographic);
  };

  const fetchContentAnalytics = () => {
    console.log('📊 Content filter changed - refreshing content analytics');
    const visitorParams = {
      metric: visitorMetric,
      page: visitorPage,
      type: visitorType
    };
    refreshAllAnalytics(dateRange, visitorParams, followerDemographic);
  };


  // ✅ TRIGGER ALL ANALYTICS ON MOUNT (ONLY ONCE)
  useEffect(() => {
    console.log('📍 Analytics page mounted');
    const visitorParams = {
      metric: visitorMetric,
      page: visitorPage,
      type: visitorType
    };
    fetchAllAnalytics(dateRange, visitorParams, followerDemographic);
  }, []); // Run once on mount

  // Sync URL with activeTab
  useEffect(() => {
    const urlTab = tab || 'content';
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [tab]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    
    if (newTab === 'content') {
      navigate('/analytics');
    } else {
      navigate(`/analytics/${newTab}`);
    }
  };

  // ✅ HANDLE APPLY BUTTON - Refresh all data
  const handleApplyDateRange = () => {
    console.log('📅 Apply button clicked - refreshing all analytics');
    setDateRange(tempDateRange);
    setShowDatePicker(false);
    
    const visitorParams = {
      metric: visitorMetric,
      page: visitorPage,
      type: visitorType
    };
    
    refreshAllAnalytics(tempDateRange, visitorParams, followerDemographic);
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${N8N_BASE_URL}/export-analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dateRange, tab: activeTab })
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${activeTab}-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const DateRangePicker = () => (
    <div className="relative">
      <button 
        onClick={() => setShowDatePicker(!showDatePicker)}
        disabled={isLoading} // ✅ DISABLE WHILE LOADING
        className={`px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 ${
          isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {new Date(dateRange.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {new Date(dateRange.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        <ChevronDown size={16} />
      </button>
      
      {showDatePicker && !isLoading && ( // ✅ HIDE PICKER WHILE LOADING
        <div className="absolute top-12 right-0 z-10 bg-white border rounded-lg shadow-lg p-4">
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600">Start Date</label>
              <input 
                type="date" 
                value={tempDateRange.start}
                onChange={(e) => setTempDateRange({...tempDateRange, start: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600">End Date</label>
              <input 
                type="date" 
                value={tempDateRange.end}
                onChange={(e) => setTempDateRange({...tempDateRange, end: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div className="flex gap-2">
              <button 
                onClick={handleApplyDateRange}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
              >
                Apply
              </button>
              <button 
                onClick={() => setShowDatePicker(false)}
                className="px-4 py-2 bg-gray-200 rounded-md text-sm hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const ContentTab = () => {
    if (loadingContent || !contentData) {
      return (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-500 text-lg">Loading content analytics...</p>
        </div>
      );
    }

    // ✅ CHECK ACCOUNT TYPE
    const isPersonalAccount = contentData?.accountType === 'personal';

    // ========== PERSONAL ACCOUNT UI ==========
    if (isPersonalAccount) {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-6 items-start">
            {/* Chart */}
            <div className="col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Performance</h3>
              <div className="h-[30rem] border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
                <svg className="w-full h-full pb-10">
                  {contentData?.chartData?.map((point, index) => {
                    const maxValue = Math.max(...(contentData?.chartData?.map(p => p.value) || [1]));
                    const x = (index / Math.max(1, contentData.chartData.length - 1)) * 100;
                    const y = 100 - (point.value / maxValue) * 70;
                    const nextPoint = contentData.chartData[index + 1];
                    
                    return nextPoint ? (
                      <line
                        key={index}
                        x1={`${x}%`}
                        y1={`${y}%`}
                        x2={`${(index + 1) / (contentData.chartData.length - 1) * 100}%`}
                        y2={`${100 - (nextPoint.value / maxValue) * 70}%`}
                        stroke="#3b82f6"
                        strokeWidth="2"
                      />
                    ) : null;
                  })}
                  {contentData?.chartData?.map((point, index) => {
                    const maxValue = Math.max(...(contentData?.chartData?.map(p => p.value) || [1]));
                    const x = (index / Math.max(1, contentData.chartData.length - 1)) * 100;
                    const y = 100 - (point.value / maxValue) * 70;
                    return (
                      <circle
                        key={`dot-${index}`}
                        cx={`${x}%`}
                        cy={`${y}%`}
                        r="4"
                        fill="#3b82f6"
                      />
                    );
                  })}
                </svg>
                <div className="absolute bottom-2 left-0 right-0 flex justify-between text-xs text-gray-500 px-6">
                  {contentData?.chartData?.map((point, index) => (
                    <div key={index} className="text-center">
                      <div>{point.date}</div>
                      <div className="text-gray-400 text-[10px]">2025</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right sidebar - Personal Account Highlights */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-2xl font-bold">{contentData?.highlights?.impressions?.value || 0}</div>
                  <div className="text-sm text-green-600 flex items-center gap-1">
                    {contentData?.highlights?.impressions?.change || '0%'}
                    ↑
                  </div>
                </div>
                <div className="text-sm text-gray-600">Impressions</div>
              </div>

              {/* Discovery Section */}
              <div className="bg-gray-100 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Discovery</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-sm text-gray-600">Impressions</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        {contentData?.discovery?.changeImpressions || '0%'}
                        <span className="text-green-500">↑</span>
                      </div>
                    </div>
                    <div className="text-lg font-bold">{contentData?.discovery?.impressions || 0}</div>
                  </div>

                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-sm text-gray-600">Members reached</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        {contentData?.discovery?.changeMembersReached || '0%'}
                        {parseInt(contentData?.discovery?.changeMembersReached || '0') > 0 ? 
                          <span className="text-green-500">↑</span> : 
                          <span className="text-gray-400">-</span>
                        }
                      </div>
                    </div>
                    <div className="text-lg font-bold">{contentData?.discovery?.membersReached || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Top Performing Posts */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Top performing posts</h3>
              <p className="text-sm text-gray-500 mt-1">Based on impressions gained from {dateRange.start} - {dateRange.end}</p>
            </div>
            
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Post</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Date</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Impressions</th>
                </tr>
              </thead>
<tbody>
  {(() => {
    // ✅ DETECT FORMAT AND NORMALIZE
    let postsToDisplay = [];
    
    if (contentData?.posts?.[0]?.data) {
      // Company format: posts[0].data[]
      postsToDisplay = contentData.posts[0].data;
    } else if (Array.isArray(contentData?.posts)) {
      // Personal format: posts[]
      postsToDisplay = contentData.posts;
    }
    
    console.log('Posts to display:', postsToDisplay);
    
    return postsToDisplay.map((post, index) => (
      <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
        <td className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
            </div>
            <span className="text-sm text-gray-700 line-clamp-2">{post.content}</span>
          </div>
        </td>
        <td className="p-4 text-sm text-gray-700">{post.date}</td>
        <td className="p-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{post.impressions}</span>
            <span className="text-xs text-green-600">▲ Impressions</span>
          </div>
        </td>
      </tr>
    ));
  })()}
</tbody>
            </table>
          </div>
        </div>
      );
    }

    // ========== COMPANY ACCOUNT UI (UNCHANGED) ==========
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-6 items-start">
          <div className="col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="h-[30rem] border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
              <svg className="w-full h-full pb-10">
                {contentData?.chartData?.map((point, index) => {
                  const maxValue = Math.max(...(contentData?.chartData?.map(p => p.value) || [1]));
                  const x = (index / Math.max(1, contentData.chartData.length - 1)) * 100;
                  const y = 100 - (point.value / maxValue) * 70;
                  const nextPoint = contentData.chartData[index + 1];
                  
                  return nextPoint ? (
                    <line
                      key={index}
                      x1={`${x}%`}
                      y1={`${y}%`}
                      x2={`${(index + 1) / (contentData.chartData.length - 1) * 100}%`}
                      y2={`${100 - (nextPoint.value / maxValue) * 70}%`}
                      stroke="#3b82f6"
                      strokeWidth="2"
                    />
                  ) : null;
                })}
                {contentData?.chartData?.map((point, index) => {
                  const maxValue = Math.max(...(contentData?.chartData?.map(p => p.value) || [1]));
                  const x = (index / Math.max(1, contentData.chartData.length - 1)) * 100;
                  const y = 100 - (point.value / maxValue) * 70;
                  return (
                    <circle
                      key={`dot-${index}`}
                      cx={`${x}%`}
                      cy={`${y}%`}
                      r="4"
                      fill="#3b82f6"
                    />
                  );
                })}
              </svg>
              <div className="absolute bottom-2 left-0 right-0 flex justify-between text-xs text-gray-500 px-6">
                {contentData?.chartData?.map((point, index) => (
                  <div key={index} className="text-center">
                    <div>{point.date}</div>
                    <div className="text-gray-400 text-[10px]">2025</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-2xl font-bold">{contentData?.highlights?.impressions?.value || 0}</div>
                <div className="text-sm text-gray-500">{contentData?.highlights?.impressions?.change || '0%'}</div>
              </div>
              <div className="text-sm text-gray-600">Impressions</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-2xl font-bold">{contentData?.highlights?.reactions?.value || 0}</div>
                <div className="text-sm text-gray-500">{contentData?.highlights?.reactions?.change || '0%'}</div>
              </div>
              <div className="text-sm text-gray-600">Reactions</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-2xl font-bold">{contentData?.highlights?.comments?.value || 0}</div>
                <div className="text-sm text-gray-500">{contentData?.highlights?.comments?.change || '0%'}</div>
              </div>
              <div className="text-sm text-gray-600">Comments</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-2xl font-bold">{contentData?.highlights?.repost?.value || 0}</div>
                <div className="text-sm text-gray-500">{contentData?.highlights?.repost?.change || '0%'}</div>
              </div>
              <div className="text-sm text-gray-600">Reposts</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">All posts</h3>
          </div>
          
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Post</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Date</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Impressions</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Reactions</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Comments</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Reposts</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Clicks</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Digimark Score</th>
              </tr>
            </thead>
            <tbody>
              {/* ✅ HANDLE BOTH WRAPPED AND FLAT STRUCTURES */}
          {(contentData?.posts?.[0]?.data || contentData?.posts || []).map((post, index) => (
                <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                      <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
                        <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                      </div>
                      <span className="text-sm text-gray-700 max-w-md truncate">{post.content}</span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-gray-700">{post.date}</td>
                  <td className="p-4 text-sm text-gray-700">{post.impressions}</td>
                  <td className="p-4 text-sm text-gray-700">{post.reactions}</td>
                  <td className="p-4 text-sm text-gray-700">{post.comments}</td>
                  <td className="p-4 text-sm text-gray-700">{post.reposts}</td>
                  <td className="p-4 text-sm text-gray-700">{post.clicks}</td>
                  <td className="p-4 text-sm text-gray-700">{post.digimarkScore || '0%'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const VisitorsTab = () => {
    if (loadingVisitors || !visitorsData) {
      return (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-500 text-lg">Loading visitor analytics...</p>
        </div>
      );
    }

    // ✅ CHECK ACCOUNT TYPE
    const isPersonalAccount = visitorsData?.accountType === 'personal';

    // ========== PERSONAL ACCOUNT UI ==========
    if (isPersonalAccount) {
      return (
        <div className="flex gap-6">
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="flex flex-col items-center justify-center">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  {visitorsData?.highlights?.profileViewers?.value || 0}
                </h2>
                <p className="text-gray-600 text-lg mb-2">Profile viewers</p>
                <p className="text-sm text-gray-500">{visitorsData?.highlights?.profileViewers?.timeframe || 'Past 90 days'}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ========== COMPANY ACCOUNT UI (UNCHANGED) ==========
    return (
      <div className="flex gap-6">
        {/* Main content - left side */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex gap-2">
                {/* First Dropdown - Metric */}
                <div className="relative">
                  <button 
                    onClick={() => {
                      setShowMetricDropdown(!showMetricDropdown);
                      setShowPageDropdown(false);
                      setShowTypeDropdown(false);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium flex items-center gap-2"
                  >
                    {visitorMetric.charAt(0).toUpperCase() + visitorMetric.slice(1)}
                    <ChevronDown size={14} />
                  </button>
                  {showMetricDropdown && (
                    <div className="absolute top-12 left-0 z-10 bg-white border rounded-lg shadow-lg py-2 min-w-[150px]">
                      <button 
                        onClick={() => {
                          setVisitorMetric('page views');
                          setShowMetricDropdown(false);
                          fetchVisitorAnalytics();
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Page views
                      </button>
                      <button 
                        onClick={() => {
                          setVisitorMetric('unique visitors');
                          setShowMetricDropdown(false);
                          fetchVisitorAnalytics();
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Unique visitors
                      </button>
                    </div>
                  )}
                </div>

                {/* Second Dropdown - Page */}
                <div className="relative">
                  <button 
                    onClick={() => {
                      setShowPageDropdown(!showPageDropdown);
                      setShowMetricDropdown(false);
                      setShowTypeDropdown(false);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium flex items-center gap-2"
                  >
                    {visitorPage.charAt(0).toUpperCase() + visitorPage.slice(1)}
                    <ChevronDown size={14} />
                  </button>
                  {showPageDropdown && (
                    <div className="absolute top-12 left-0 z-10 bg-white border rounded-lg shadow-lg py-2 min-w-[150px]">
                      <button 
                        onClick={() => {
                          setVisitorPage('all pages');
                          setShowPageDropdown(false);
                          fetchVisitorAnalytics();
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        All pages
                      </button>
                      <button 
                        onClick={() => {
                          setVisitorPage('home');
                          setShowPageDropdown(false);
                          fetchVisitorAnalytics();
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Home
                      </button>
                      <button 
                        onClick={() => {
                          setVisitorPage('about');
                          setShowPageDropdown(false);
                          fetchVisitorAnalytics();
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        About
                      </button>
                      <button 
                        onClick={() => {
                          setVisitorPage('insights');
                          setShowPageDropdown(false);
                          fetchVisitorAnalytics();
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Insights
                      </button>
                      <button 
                        onClick={() => {
                          setVisitorPage('people');
                          setShowPageDropdown(false);
                          fetchVisitorAnalytics();
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        People
                      </button>
                    </div>
                  )}
                </div>

                {/* Third Dropdown - Visitor Type */}
                <div className="relative">
                  <button 
                    onClick={() => {
                      setShowTypeDropdown(!showTypeDropdown);
                      setShowMetricDropdown(false);
                      setShowPageDropdown(false);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium flex items-center gap-2"
                  >
                    {visitorType.charAt(0).toUpperCase() + visitorType.slice(1)}
                    <ChevronDown size={14} />
                  </button>
                  {showTypeDropdown && (
                    <div className="absolute top-12 left-0 z-10 bg-white border rounded-lg shadow-lg py-2 min-w-[150px]">
                      <button 
                        onClick={() => {
                          setVisitorType('all visitors');
                          setShowTypeDropdown(false);
                          fetchVisitorAnalytics();
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        All visitors
                      </button>
                      <button 
                        onClick={() => {
                          setVisitorType('job seekers');
                          setShowTypeDropdown(false);
                          fetchVisitorAnalytics();
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Job seekers
                      </button>
                    </div>
                  )}
                </div>

                <button className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-full text-sm font-medium">
                  All filters
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="h-80 border border-gray-200 rounded-lg p-6 bg-gray-50 relative">
                {/* Y-axis labels */}
                {visitorsData?.chartData?.desktop && (() => {
                  const maxValue = Math.max(
                    ...visitorsData.chartData.desktop.map(p => p.value),
                    ...visitorsData.chartData.mobile.map(p => p.value),
                    1
                  );
                  const yAxisValues = [
                    maxValue,
                    Math.round(maxValue * 0.75),
                    Math.round(maxValue * 0.5),
                    Math.round(maxValue * 0.25),
                    0
                  ];
                  
                  return (
                    <div className="absolute left-0 top-6 bottom-12 flex flex-col justify-between text-xs text-gray-500 w-8">
                      {yAxisValues.map((val, idx) => (
                        <div key={idx} className="text-right pr-2">
                          {val}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <svg className="w-full h-full pl-8 pb-8" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {/* Grid lines */}
                  <line x1="0" y1="0" x2="100" y2="0" stroke="#e5e7eb" strokeWidth="0.2" />
                  <line x1="0" y1="20" x2="100" y2="20" stroke="#e5e7eb" strokeWidth="0.2" />
                  <line x1="0" y1="40" x2="100" y2="40" stroke="#e5e7eb" strokeWidth="0.2" />
                  <line x1="0" y1="60" x2="100" y2="60" stroke="#e5e7eb" strokeWidth="0.2" />
                  <line x1="0" y1="80" x2="100" y2="80" stroke="#e5e7eb" strokeWidth="0.2" />
                  <line x1="0" y1="100" x2="100" y2="100" stroke="#e5e7eb" strokeWidth="0.2" />

                  {/* Desktop line (blue) */}
                  {visitorsData?.chartData?.desktop && (() => {
                    const maxValue = Math.max(
                      ...visitorsData.chartData.desktop.map(p => p.value),
                      ...visitorsData.chartData.mobile.map(p => p.value),
                      1
                    );
                    return (
                      <>
                        {visitorsData.chartData.desktop.map((point, index) => {
                          const nextPoint = visitorsData.chartData.desktop[index + 1];
                          if (!nextPoint) return null;
                          
                          const x1 = (index / (visitorsData.chartData.desktop.length - 1)) * 100;
                          const y1 = 100 - (point.value / maxValue) * 90;
                          const x2 = ((index + 1) / (visitorsData.chartData.desktop.length - 1)) * 100;
                          const y2 = 100 - (nextPoint.value / maxValue) * 90;
                          
                          return (
                            <line
                              key={`desktop-${index}`}
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke="#6366f1"
                              strokeWidth="0.2"
                            />
                          );
                        })}
                        {/* Area fill for desktop */}
                        <polygon
                          points={visitorsData.chartData.desktop.map((point, index) => {
                            const x = (index / (visitorsData.chartData.desktop.length - 1)) * 100;
                            const y = 100 - (point.value / maxValue) * 90;
                            return `${x},${y}`;
                          }).join(' ') + ` 100,100 0,100`}
                          fill="#6366f1"
                          fillOpacity="0.0"
                        />
                        {visitorsData.chartData.desktop.map((point, index) => {
                          const x = (index / (visitorsData.chartData.desktop.length - 1)) * 100;
                          const y = 100 - (point.value / maxValue) * 90;
                          return (
                            <circle
                              key={`desktop-dot-${index}`}
                              cx={x}
                              cy={y}
                              r="0.2"
                              fill="#6366f1"
                            />
                          );
                        })}
                      </>
                    );
                  })()}

                  {/* Mobile line (green) */}
                  {visitorsData?.chartData?.mobile && (() => {
                    const maxValue = Math.max(
                      ...visitorsData.chartData.desktop.map(p => p.value),
                      ...visitorsData.chartData.mobile.map(p => p.value),
                      1
                    );
                    return (
                      <>
                        {visitorsData.chartData.mobile.map((point, index) => {
                          const nextPoint = visitorsData.chartData.mobile[index + 1];
                          if (!nextPoint) return null;
                          
                          const x1 = (index / (visitorsData.chartData.mobile.length - 1)) * 100;
                          const y1 = 100 - (point.value / maxValue) * 90;
                          const x2 = ((index + 1) / (visitorsData.chartData.mobile.length - 1)) * 100;
                          const y2 = 100 - (nextPoint.value / maxValue) * 90;
                          
                          return (
                            <line
                              key={`mobile-${index}`}
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke="#10b981"
                              strokeWidth="0.2"
                            />
                          );
                        })}
                        {/* Area fill for mobile */}
                        <polygon
                          points={visitorsData.chartData.mobile.map((point, index) => {
                            const x = (index / (visitorsData.chartData.mobile.length - 1)) * 100;
                            const y = 100 - (point.value / maxValue) * 90;
                            return `${x},${y}`;
                          }).join(' ') + ` 100,100 0,100`}
                          fill="#10b981"
                          fillOpacity="0.15"
                        />
                        {visitorsData.chartData.mobile.map((point, index) => {
                          const x = (index / (visitorsData.chartData.mobile.length - 1)) * 100;
                          const y = 100 - (point.value / maxValue) * 90;
                          return (
                            <circle
                              key={`mobile-dot-${index}`}
                              cx={x}
                              cy={y}
                              r="0.2"
                              fill="#10b981"
                            />
                          );
                        })}
                      </>
                    );
                  })()}
                </svg>

                {/* X-axis labels - Show every 5th date or calculate interval */}
                <div className="absolute bottom-0 left-8 right-0 flex justify-between text-xs text-gray-500 px-6 pb-2">
                  {visitorsData?.chartData?.desktop && (() => {
                    const dataLength = visitorsData.chartData.desktop.length;
                    // Calculate interval to show approximately 6-8 dates
                    const interval = Math.max(1, Math.floor(dataLength / 7));
                    const displayDates = [];
                    
                    for (let i = 0; i < dataLength; i += interval) {
                      displayDates.push(
                        <div key={i} className="text-center" style={{ width: `${100 / Math.ceil(dataLength / interval)}%` }}>
                          {visitorsData.chartData.desktop[i].date}
                        </div>
                      );
                    }
                    
                    // Always show the last date if it's not already included
                    if ((dataLength - 1) % interval !== 0) {
                      displayDates.push(
                        <div key={dataLength - 1} className="text-center" style={{ width: `${100 / Math.ceil(dataLength / interval)}%` }}>
                          {visitorsData.chartData.desktop[dataLength - 1].date}
                        </div>
                      );
                    }
                    
                    return displayDates;
                  })()}
                </div>

                {/* Legend */}
                <div className="absolute top-2 right-6 flex gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-0.5 bg-blue-500"></div>
                    <span className="text-gray-600">Desktop</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-0.5 bg-green-500"></div>
                    <span className="text-gray-600">Mobile</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right sidebar */}
        <div className="w-80 space-y-4">
          {/* Visitor highlights */}
          <div className="bg-gray-100 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Visitor highlights</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm text-gray-600">Page views</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    {visitorsData?.highlights?.pageViews?.change || '0'}
                    <span className="text-red-500">↓</span>
                  </div>
                </div>
                <div className="text-lg font-bold">{visitorsData?.highlights?.pageViews?.value || 0}</div>
              </div>

              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm text-gray-600">Unique visitors</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    {visitorsData?.highlights?.uniqueVisitors?.change || '0'}
                    <span className="text-red-500">↓</span>
                  </div>
                </div>
                <div className="text-lg font-bold">{visitorsData?.highlights?.uniqueVisitors?.value || 0}</div>
              </div>

              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm text-gray-600">Custom button clicks</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    {visitorsData?.highlights?.customButtonClicks?.change || '0'}
                    <span className="text-red-500">↓</span>
                  </div>
                </div>
                <div className="text-lg font-bold">{visitorsData?.highlights?.customButtonClicks?.value || 0}</div>
              </div>
            </div>
          </div>

          {/* Visitor Demographics */}
          <div className="bg-gray-100 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Visitor Demographics</h3>
            <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm mb-4">
              <option>Job function</option>
            </select>
            <div className="space-y-4">
              {visitorsData?.demographics?.map((demo, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-700">{demo.name}</span>
                    <span className="text-sm font-semibold">{demo.value}</span>
                  </div>
                  <div className="w-full bg-gray-300 rounded-full h-1.5">
                    <div 
                      className="bg-blue-600 h-1.5 rounded-full" 
                      style={{ width: `${demo.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

const FollowersTab = () => {
    if (loadingFollowers || !followersData) {
      return (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-500 text-lg">Loading follower analytics...</p>
        </div>
      );
    }

    // ✅ CHECK ACCOUNT TYPE
    const isPersonalAccount = followersData?.accountType === 'personal';

    // ========== PERSONAL ACCOUNT UI ==========
    if (isPersonalAccount) {
      return (
        <div className="flex gap-6">
          {/* Main content - left side */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">New followers</h3>
                <p className="text-sm text-gray-500 mt-1">Past 7 days</p>
              </div>

              <div className="p-6">
                <div className="h-80 border border-gray-200 rounded-lg p-6 bg-gray-50 relative">
                  {/* Y-axis labels */}
                  {followersData?.chartData && followersData.chartData.length > 0 && (() => {
                    const maxValue = Math.max(...followersData.chartData.map(p => p.value), 1);
                    const yAxisValues = [
                      maxValue,
                      Math.round(maxValue * 0.75),
                      Math.round(maxValue * 0.5),
                      Math.round(maxValue * 0.25),
                      0
                    ];
                    
                    return (
                      <div className="absolute left-0 top-6 bottom-12 flex flex-col justify-between text-xs text-gray-500 w-8">
                        {yAxisValues.map((val, idx) => (
                          <div key={idx} className="text-right pr-2">
                            {val}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  <svg className="w-full h-full pl-8 pb-8" viewBox="0 0 100 100" preserveAspectRatio="none">
                    {/* Grid lines */}
                    <line x1="0" y1="0" x2="100" y2="0" stroke="#e5e7eb" strokeWidth="0.2" />
                    <line x1="0" y1="20" x2="100" y2="20" stroke="#e5e7eb" strokeWidth="0.2" />
                    <line x1="0" y1="40" x2="100" y2="40" stroke="#e5e7eb" strokeWidth="0.2" />
                    <line x1="0" y1="60" x2="100" y2="60" stroke="#e5e7eb" strokeWidth="0.2" />
                    <line x1="0" y1="80" x2="100" y2="80" stroke="#e5e7eb" strokeWidth="0.2" />
                    <line x1="0" y1="100" x2="100" y2="100" stroke="#e5e7eb" strokeWidth="0.2" />

                    {/* Follower line (blue) */}
                    {followersData?.chartData && followersData.chartData.length > 0 && (() => {
                      const maxValue = Math.max(...followersData.chartData.map(p => p.value), 1);
                      return (
                        <>
                          {followersData.chartData.map((point, index) => {
                            const nextPoint = followersData.chartData[index + 1];
                            if (!nextPoint) return null;
                            
                            const x1 = (index / (followersData.chartData.length - 1)) * 100;
                            const y1 = 100 - (point.value / maxValue) * 90;
                            const x2 = ((index + 1) / (followersData.chartData.length - 1)) * 100;
                            const y2 = 100 - (nextPoint.value / maxValue) * 90;
                            
                            return (
                              <line
                                key={`follower-${index}`}
                                x1={x1}
                                y1={y1}
                                x2={x2}
                                y2={y2}
                                stroke="#3b82f6"
                                strokeWidth="0.2"
                              />
                            );
                          })}
                          {followersData.chartData.map((point, index) => {
                            const x = (index / (followersData.chartData.length - 1)) * 100;
                            const y = 100 - (point.value / maxValue) * 90;
                            return (
                              <circle
                                key={`follower-dot-${index}`}
                                cx={x}
                                cy={y}
                                r="0.5"
                                fill="#3b82f6"
                              />
                            );
                          })}
                        </>
                      );
                    })()}
                  </svg>

                  {/* X-axis labels */}
                  <div className="absolute bottom-0 left-8 right-0 flex justify-between text-xs text-gray-500 px-6 pb-2">
                    {followersData?.chartData && (() => {
                      const dataLength = followersData.chartData.length;
                      const interval = Math.max(1, Math.floor(dataLength / 7));
                      const displayDates = [];
                      
                      for (let i = 0; i < dataLength; i += interval) {
                        displayDates.push(
                          <div key={i} className="text-center" style={{ width: `${100 / Math.ceil(dataLength / interval)}%` }}>
                            {followersData.chartData[i].date}
                          </div>
                        );
                      }
                      
                      if ((dataLength - 1) % interval !== 0) {
                        displayDates.push(
                          <div key={dataLength - 1} className="text-center" style={{ width: `${100 / Math.ceil(dataLength / interval)}%` }}>
                            {followersData.chartData[dataLength - 1].date}
                          </div>
                        );
                      }
                      
                      return displayDates;
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right sidebar - Personal Account */}
          <div className="w-80 space-y-4">
            {/* Follower Summary */}
            <div className="bg-gray-100 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <div className="text-2xl font-bold">{followersData?.highlights?.totalFollowers || 0}</div>
                <div className="text-sm text-green-600 flex items-center gap-1">
                  {followersData?.highlights?.newFollowersChange || '0%'}
                  {parseInt(followersData?.highlights?.newFollowersChange || '0') > 0 ? '↑' : '-'}
                </div>
              </div>
              <div className="text-sm text-gray-600">Total followers</div>
              <div className="text-xs text-gray-500 mt-1">vs. prior 7 days</div>
            </div>

            {/* Follower Demographics */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Top demographics of followers</h3>
              <div className="space-y-4">
                {followersData?.demographics?.map((demo, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center mb-1">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{demo.name}</div>
                        {demo.category && (
                          <div className="text-xs text-gray-500">{demo.category}</div>
                        )}
                      </div>
                      <span className="text-sm font-semibold">{demo.percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-300 rounded-full h-1.5">
                      <div 
                        className="bg-blue-600 h-1.5 rounded-full" 
                        style={{ width: `${demo.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ========== COMPANY ACCOUNT UI (UNCHANGED) ==========
    return (
      <div className="flex gap-6">
        {/* Main content - left side */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Follower metrics</h3>
            </div>

            <div className="p-6">
              <div className="h-80 border border-gray-200 rounded-lg p-6 bg-gray-50 relative">
                {/* Y-axis labels */}
                {followersData?.chartData?.organic && (() => {
                  const maxValue = Math.max(
                    ...followersData.chartData.organic.map(p => p.value),
                    ...followersData.chartData.sponsored.map(p => p.value),
                    1
                  );
                  const yAxisValues = [
                    maxValue,
                    Math.round(maxValue * 0.75),
                    Math.round(maxValue * 0.5),
                    Math.round(maxValue * 0.25),
                    0
                  ];
                  
                  return (
                    <div className="absolute left-0 top-6 bottom-12 flex flex-col justify-between text-xs text-gray-500 w-8">
                      {yAxisValues.map((val, idx) => (
                        <div key={idx} className="text-right pr-2">
                          {val}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <svg className="w-full h-full pl-8 pb-8" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {/* Grid lines */}
                  <line x1="0" y1="0" x2="100" y2="0" stroke="#e5e7eb" strokeWidth="0.2" />
                  <line x1="0" y1="20" x2="100" y2="20" stroke="#e5e7eb" strokeWidth="0.2" />
                  <line x1="0" y1="40" x2="100" y2="40" stroke="#e5e7eb" strokeWidth="0.2" />
                  <line x1="0" y1="60" x2="100" y2="60" stroke="#e5e7eb" strokeWidth="0.2" />
                  <line x1="0" y1="80" x2="100" y2="80" stroke="#e5e7eb" strokeWidth="0.2" />
                  <line x1="0" y1="100" x2="100" y2="100" stroke="#e5e7eb" strokeWidth="0.2" />

                  {/* Organic line (blue) */}
                  {followersData?.chartData?.organic && (() => {
                    const maxValue = Math.max(
                      ...followersData.chartData.organic.map(p => p.value),
                      ...followersData.chartData.sponsored.map(p => p.value),
                      1
                    );
                    return (
                      <>
                        {followersData.chartData.organic.map((point, index) => {
                          const nextPoint = followersData.chartData.organic[index + 1];
                          if (!nextPoint) return null;
                          
                          const x1 = (index / (followersData.chartData.organic.length - 1)) * 100;
                          const y1 = 100 - (point.value / maxValue) * 90;
                          const x2 = ((index + 1) / (followersData.chartData.organic.length - 1)) * 100;
                          const y2 = 100 - (nextPoint.value / maxValue) * 90;
                          
                          return (
                            <line
                              key={`organic-${index}`}
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke="#3b82f6"
                              strokeWidth="0.2"
                            />
                          );
                        })}
                        {followersData.chartData.organic.map((point, index) => {
                          const x = (index / (followersData.chartData.organic.length - 1)) * 100;
                          const y = 100 - (point.value / maxValue) * 90;
                          return (
                            <circle
                              key={`organic-dot-${index}`}
                              cx={x}
                              cy={y}
                              r="0.5"
                              fill="#3b82f6"
                            />
                          );
                        })}
                      </>
                    );
                  })()}

                  {/* Sponsored line (if data exists) */}
                  {followersData?.chartData?.sponsored && followersData.chartData.sponsored.some(p => p.value > 0) && (() => {
                    const maxValue = Math.max(
                      ...followersData.chartData.organic.map(p => p.value),
                      ...followersData.chartData.sponsored.map(p => p.value),
                      1
                    );
                    return (
                      <>
                        {followersData.chartData.sponsored.map((point, index) => {
                          const nextPoint = followersData.chartData.sponsored[index + 1];
                          if (!nextPoint) return null;
                          
                          const x1 = (index / (followersData.chartData.sponsored.length - 1)) * 100;
                          const y1 = 100 - (point.value / maxValue) * 90;
                          const x2 = ((index + 1) / (followersData.chartData.sponsored.length - 1)) * 100;
                          const y2 = 100 - (nextPoint.value / maxValue) * 90;
                          
                          return (
                            <line
                              key={`sponsored-${index}`}
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke="#10b981"
                              strokeWidth="0.8"
                            />
                          );
                        })}
                        {followersData.chartData.sponsored.map((point, index) => {
                          const x = (index / (followersData.chartData.sponsored.length - 1)) * 100;
                          const y = 100 - (point.value / maxValue) * 90;
                          return (
                            <circle
                              key={`sponsored-dot-${index}`}
                              cx={x}
                              cy={y}
                              r="1"
                              fill="#10b981"
                            />
                          );
                        })}
                      </>
                    );
                  })()}
                </svg>

                {/* X-axis labels - Show every ~5th date */}
                <div className="absolute bottom-0 left-8 right-0 flex justify-between text-xs text-gray-500 px-6 pb-2">
                  {followersData?.chartData?.organic && (() => {
                    const dataLength = followersData.chartData.organic.length;
                    const interval = Math.max(1, Math.floor(dataLength / 7));
                    const displayDates = [];
                    
                    for (let i = 0; i < dataLength; i += interval) {
                      displayDates.push(
                        <div key={i} className="text-center" style={{ width: `${100 / Math.ceil(dataLength / interval)}%` }}>
                          {followersData.chartData.organic[i].date}
                        </div>
                      );
                    }
                    
                    if ((dataLength - 1) % interval !== 0) {
                      displayDates.push(
                        <div key={dataLength - 1} className="text-center" style={{ width: `${100 / Math.ceil(dataLength / interval)}%` }}>
                          {followersData.chartData.organic[dataLength - 1].date}
                        </div>
                      );
                    }
                    
                    return displayDates;
                  })()}
                </div>

                {/* Legend */}
                <div className="absolute top-2 right-6 flex gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-0.5 bg-blue-500"></div>
                    <span className="text-gray-600">Organic</span>
                  </div>
                  {followersData?.chartData?.sponsored && followersData.chartData.sponsored.some(p => p.value > 0) && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-green-500"></div>
                      <span className="text-gray-600">Sponsored</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right sidebar */}
        <div className="w-80 space-y-4">
          {/* Follower highlights */}
          <div className="bg-gray-100 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Follower highlights</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm text-gray-600">Total followers</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    {followersData?.highlights?.totalFollowers?.change || '0'}
                    {parseInt(followersData?.highlights?.totalFollowers?.change || '0') > 0 ? 
                      <span className="text-green-500">↑</span> : 
                      <span className="text-gray-400">-</span>
                    }
                  </div>
                </div>
                <div className="text-lg font-bold">{followersData?.highlights?.totalFollowers?.value || 0}</div>
              </div>

              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm text-gray-600">New followers in last 30 days</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    {followersData?.highlights?.organicFollowers?.change || '0'}%
                    {parseInt(followersData?.highlights?.organicFollowers?.change || '0') > 0 ? 
                      <span className="text-green-500">↑</span> : 
                      <span className="text-gray-400">-</span>
                    }
                  </div>
                </div>
                <div className="text-lg font-bold">{followersData?.highlights?.organicFollowers?.value || 0}</div>
              </div>
            </div>
          </div>

          {/* Follower Demographics */}
          <div className="bg-gray-100 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Follower Demographics</h3>
            <select 
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm mb-4"
              value={followerDemographic}
              onChange={(e) => setFollowerDemographic(e.target.value)}
            >
              <option value="Location">Location</option>
              <option value="Industry">Industry</option>
              <option value="Job function">Job function</option>
              <option value="Company size">Company size</option>
              <option value="Seniority">Seniority</option>
            </select>
            <div className="space-y-4">
              {followersData?.demographics?.map((demo, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-700">{demo.name}</span>
                    <span className="text-sm font-semibold">{demo.count}</span>
                  </div>
                  <div className="w-full bg-gray-300 rounded-full h-1.5">
                    <div 
                      className="bg-blue-600 h-1.5 rounded-full" 
                      style={{ 
                        width: `${Math.min(100, (demo.count / (followersData?.highlights?.totalFollowers?.value || 1)) * 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

 const LeadsTab = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
      <div className="flex flex-col items-center justify-center py-12">
        <BarChart3 size={64} className="text-gray-300 mb-6" />
        <h3 className="text-2xl font-semibold text-gray-800 mb-3">Leads Analytics Coming Soon</h3>
        <p className="text-gray-500 text-lg">This section is currently under development and will be available soon.</p>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        <div className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-2">
              <button 
                onClick={() => handleTabChange('content')} 
                className={`px-6 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'content' 
                    ? 'bg-white text-gray-1200 border border-gray-500' 
                    : 'bg-transparent text-gray-600'
                }`}
              >
                Content
              </button>
              <button 
                onClick={() => handleTabChange('visitors')}
                className={`px-6 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'visitors' 
                    ? 'bg-white text-gray-1200 border border-gray-500' 
                    : 'bg-transparent text-gray-600'
                }`}
              >
                Visitors
              </button>
              <button 
                onClick={() => handleTabChange('followers')}
                className={`px-6 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'followers' 
                    ? 'bg-white text-gray-1200 border border-gray-500' 
                    : 'bg-transparent text-gray-600'
                }`}
              >
                Followers
              </button>
              {/* <button 
                onClick={() => handleTabChange('leads')}
                className={`px-6 py-2 rounded-md text-sm font-medium ${
                  activeTab === 'leads' 
                    ? 'bg-white text-gray-1200 border border-gray-500' 
                    : 'bg-transparent text-gray-600'
                }`}
              >
                Leads
              </button> */}
            </div>

            <div className="flex gap-3">
              <DateRangePicker />
              
              {/* <button 
                onClick={handleExport}
                disabled={isLoading} // ✅ DISABLE EXPORT WHILE LOADING
                className={`px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium flex items-center gap-2 hover:bg-blue-700 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Export
                <Download size={16} />
              </button> */}
            </div>
          </div>

          {activeTab === 'content' && <ContentTab />}
          {activeTab === 'visitors' && <VisitorsTab />}
          {activeTab === 'followers' && <FollowersTab />}
          {activeTab === 'leads' && <LeadsTab />}
        </div>
      </div>
    </div>
  );
};

export default Analytics;